import React, { useState, useEffect, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { UserService, User } from '../../services/user';
import { resolveTeamName } from '../../utils/userNameResolver';

interface TeamPerformanceTrendsChartProps {
  data: Array<{
    month: string;
    [key: string]: string | number; // Dynamic team names as keys
  }>;
  teams: (string | number)[];
  loading?: boolean;
}

const TeamPerformanceTrendsChart: React.FC<TeamPerformanceTrendsChartProps> = ({ data, teams, loading = false }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);

  // Load users on mount
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const users = await UserService.getAllDirectoryUsers();
        setUsers(users);
      } catch (error) {
        console.error('Failed to load users:', error);
      } finally {
        setUsersLoading(false);
      }
    };
    loadUsers();
  }, []);

  // Create a map of user ID to user name
  const userIdToNameMap = useMemo(() => {
    const map = new Map<number, string>();
    users.forEach(user => {
      map.set(user.id, user.full_name || user.username);
    });
    return map;
  }, [users]);

  // Resolve team names (convert IDs to names)
  const resolvedTeams = useMemo(() => {
    return teams.map(team => resolveTeamName(team, userIdToNameMap));
  }, [teams, userIdToNameMap]);

  // Create a mapping from original team identifier to resolved name
  const teamNameMap = useMemo(() => {
    const map = new Map<string | number, string>();
    teams.forEach((team, index) => {
      map.set(team, resolvedTeams[index]);
    });
    return map;
  }, [teams, resolvedTeams]);

  // Resolve data keys (convert team IDs in data keys to names)
  const resolvedData = useMemo(() => {
    return data.map(item => {
      const resolved: any = { month: item.month };
      Object.keys(item).forEach(key => {
        if (key !== 'month') {
          // Check if this key matches a team (could be string name or stringified number ID)
          let resolvedKey = key;
          
          // First, try to find it in the teams array
          const originalTeam = teams.find(t => String(t) === key);
          if (originalTeam !== undefined) {
            resolvedKey = teamNameMap.get(originalTeam) || key;
          } else {
            // If not found in teams array, check if key is a numeric string (like "126")
            const numericKey = Number(key);
            if (!isNaN(numericKey) && isFinite(numericKey) && key.trim() === String(numericKey)) {
              // It's a stringified number ID, resolve it
              const resolvedName = userIdToNameMap.get(numericKey);
              if (resolvedName) {
                resolvedKey = resolvedName;
              }
            }
          }
          
          resolved[resolvedKey] = item[key];
        }
      });
      return resolved;
    });
  }, [data, teams, teamNameMap, userIdToNameMap]);

  if (loading || usersLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Team Performance Trends</h3>
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0 || !teams || teams.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Team Performance Trends</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No team performance trend data available
        </div>
      </div>
    );
  }

  const formatMonth = (month: string) => {
    const date = new Date(month);
    return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };

  const colors = [
    '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', 
    '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#6366f1'
  ];

  const getTeamColor = (index: number) => colors[index % colors.length];

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Team Performance Trends</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={resolvedData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="month" 
              tick={{ fontSize: 12 }}
              tickFormatter={formatMonth}
            />
            <YAxis 
              tick={{ fontSize: 12 }}
              domain={[0, 100]}
            />
            <Tooltip 
              formatter={(value: number, name: string) => [`${value.toFixed(1)}%`, name]}
              labelFormatter={(label) => `Month: ${formatMonth(label)}`}
            />
            <Legend />
            {resolvedTeams.map((team, index) => (
              <Line
                key={`${team}-${index}`}
                type="monotone"
                dataKey={team}
                stroke={getTeamColor(index)}
                strokeWidth={2}
                name={team}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Team Legend</h4>
          <div className="space-y-2">
            {resolvedTeams.map((team, index) => {
              const latestData = resolvedData[resolvedData.length - 1];
              const currentPerformance = latestData ? latestData[team] as number : 0;
              return (
                <div key={`${team}-legend-${index}`} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <div className="flex items-center">
                    <div 
                      className="w-3 h-3 rounded mr-2" 
                      style={{ backgroundColor: getTeamColor(index) }}
                    ></div>
                    <span className="text-sm font-medium">{team}</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">{currentPerformance.toFixed(1)}%</span>
                    <span className="ml-2 text-xs text-gray-500">current</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Trend Analysis</h4>
          <div className="space-y-3">
            {resolvedTeams.map((team, index) => {
              const firstData = resolvedData[0];
              const lastData = resolvedData[resolvedData.length - 1];
              const firstPerformance = firstData ? firstData[team] as number : 0;
              const lastPerformance = lastData ? lastData[team] as number : 0;
              const trend = lastPerformance - firstPerformance;
              const isImproving = trend > 0;
              
              return (
                <div key={`${team}-trend-${index}`} className={`p-3 rounded-lg ${isImproving ? 'bg-green-50' : 'bg-red-50'}`}>
                  <div className="flex justify-between items-center">
                    <span className={`text-sm ${isImproving ? 'text-green-600' : 'text-red-600'}`}>
                      {team}
                    </span>
                    <span className={`font-bold ${isImproving ? 'text-green-600' : 'text-red-600'}`}>
                      {trend > 0 ? '+' : ''}{trend.toFixed(1)}%
                    </span>
                  </div>
                  <div className={`text-xs ${isImproving ? 'text-green-500' : 'text-red-500'} mt-1`}>
                    {isImproving ? 'Improving' : 'Declining'} over time
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamPerformanceTrendsChart;
